import { SearchIcon, Spinner } from '@va/icons';
import { useTranslate } from '@va/localization';
import { DNSRecord } from '@va/shared/api/custom-domains';
import { DNSRecordsTable, DNSVerificationStatusMap } from '@va/shared/custom-domains';
import { Button, ButtonColors } from '@va/ui/design-system';
import { useRef } from 'react';
import { useVerifyDNSRecords } from './useVerifyDNSRecords';

export const VerifyDnsSection = ({
  domain,
  dnsRecords,
  isLoading,
  setIsLoading,
  dnsVerificationStatusMap,
  setDnsVerificationStatusMap,
  onSuccess,
  onFailure,
  buttonColor,
  expectedRecordsCount,
}: {
  domain: string;
  dnsRecords: DNSRecord[] | undefined;
  isLoading: boolean;
  setIsLoading: (b: boolean) => void;
  dnsVerificationStatusMap: DNSVerificationStatusMap;
  setDnsVerificationStatusMap: (d: DNSVerificationStatusMap) => void;
  onSuccess?: () => void;
  onFailure?: (err: string) => void;
  buttonColor?: ButtonColors;
  expectedRecordsCount: number;
}) => {
  const resetTimeout = useRef<NodeJS.Timeout | null>(null);

  const { verify } = useVerifyDNSRecords();

  const translate = useTranslate();

  return (
    <>
      <DNSRecordsTable
        dnsVerificationStatusMap={dnsVerificationStatusMap}
        customDomain={domain}
        expectedRecordsCount={expectedRecordsCount}
        records={dnsRecords}
      />
      <Button
        onClick={async () => {
          if (!dnsRecords || dnsRecords.length === 0) return;
          try {
            setIsLoading(true);
            setDnsVerificationStatusMap({});
            if (resetTimeout.current) {
              clearTimeout(resetTimeout.current);
            }

            const { statusMap, someFailed } = await verify({ dnsRecords: dnsRecords, domain: domain });

            setDnsVerificationStatusMap(statusMap);
            resetTimeout.current = setTimeout(() => {
              setDnsVerificationStatusMap({});
            }, 3000);

            if (someFailed) {
              onFailure?.(translate('errors.dnsCheckFailed'));
            } else {
              onSuccess?.();
            }
          } catch (error) {
            console.error(error);
            onFailure?.(translate('notifications.generalError'));
          } finally {
            setIsLoading(false);
          }
        }}
        disabled={!dnsRecords || dnsRecords.length === 0 || isLoading}
        className='w-full justify-center'
        color={buttonColor}
        icon={(_, color) => (isLoading ? <Spinner /> : <SearchIcon color={color} />)}
        text={translate('button.verifyDNS')}
      />
    </>
  );
};
